/**
 * useContactForm.ts
 * 
 * Hook personalizado para gestionar el formulario de agregar/editar contactos.
 * Incluye validaciones en tiempo real, normalización de datos y detección de duplicados.
 * 
 * @version 1.0.0
 * @date 2024-11-07
 */

import { useState, useCallback, useEffect } from 'react';
import { ContactDB } from '../data/mockContactsDatabase';

// ==================== 📋 INTERFACES ====================

export interface ContactFormData {
  name: string;
  phone: string;
  email: string;
  company: string;
  position: string;  // ← Corregido (en ContactDB es "positon" pero debería ser "position")
  groups: string[];
  tags: string[];
  status: 'active' | 'inactive' | 'bounced' | 'unsubscribed';
  notes: string;
}

export interface ValidationErrors {
  name?: string;
  phone?: string;
  email?: string;
  company?: string;
  position?: string;
}

export interface UseContactFormProps {
  onSuccess?: (contact: ContactDB) => void;
  onError?: (error: string) => void;
  checkDuplicatePhone?: (phone: string) => ContactDB | undefined;
  addContact?: (contact: Omit<ContactDB, 'id' | 'created_at' | 'updated_at'>) => Promise<ContactDB>;
}

export interface UseContactFormResult {
  formData: ContactFormData;
  errors: ValidationErrors;
  isSubmitting: boolean;
  isDuplicate: boolean;
  duplicateContact: ContactDB | null;
  
  // Handlers
  handleChange: (field: keyof ContactFormData, value: any) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
  
  // Validation states
  isValid: boolean;
  touchedFields: Set<keyof ContactFormData>;
  setFieldTouched: (field: keyof ContactFormData) => void;
}

// ==================== 🔧 UTILS ====================

/**
 * Normaliza número de teléfono chileno
 */
const normalizePhone = (phone: string): string => {
  // Eliminar todo excepto números y +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // Si no tiene +, agregar +56 (Chile)
  if (!cleaned.startsWith('+')) {
    // Si empieza con 56, agregar +
    if (cleaned.startsWith('56')) {
      cleaned = '+' + cleaned;
    }
    // Si es número local (9 dígitos), agregar +56
    else if (cleaned.length === 9) {
      cleaned = '+56' + cleaned;
    }
    // Otros casos, agregar +56
    else if (cleaned.length >= 8) {
      cleaned = '+56' + cleaned;
    }
  }
  
  return cleaned;
};

/**
 * Valida formato de email
 */
const validateEmail = (email: string): boolean => {
  if (!email.trim()) return true; // Email es opcional
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida teléfono chileno
 */
const validatePhone = (phone: string): boolean => {
  if (!phone.trim()) return false;
  
  const normalized = normalizePhone(phone);
  
  // Formato válido: +56XXXXXXXXX (12 caracteres mínimo)
  // +56 (3) + 9 dígitos (9) = 12
  return normalized.startsWith('+56') && normalized.length >= 12;
};

// ==================== 🎣 HOOK ====================

export const useContactForm = ({
  onSuccess,
  onError,
  checkDuplicatePhone,
  addContact
}: UseContactFormProps = {}): UseContactFormResult => {

  // ───────────────────────────────────────────────────────────────────────
  // ESTADOS
  // ───────────────────────────────────────────────────────────────────────

  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    phone: '',
    email: '',
    company: '',
    position: '',
    groups: [],
    tags: [],
    status: 'active',
    notes: ''
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Set<keyof ContactFormData>>(new Set());
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateContact, setDuplicateContact] = useState<ContactDB | null>(null);

  // ───────────────────────────────────────────────────────────────────────
  // VALIDACIONES
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Valida un campo específico
   */
  const validateField = useCallback((field: keyof ContactFormData, value: any): string | undefined => {
    switch (field) {
      case 'name':
        if (!value || value.trim().length === 0) {
          return 'El nombre es requerido';
        }
        if (value.trim().length < 2) {
          return 'El nombre debe tener al menos 2 caracteres';
        }
        if (value.trim().length > 100) {
          return 'El nombre no puede exceder 100 caracteres';
        }
        return undefined;

      case 'phone':
        if (!value || value.trim().length === 0) {
          return 'El teléfono es requerido';
        }
        if (!validatePhone(value)) {
          return 'Formato inválido. Ejemplo: +56912345678';
        }
        return undefined;

      case 'email':
        if (value && !validateEmail(value)) {
          return 'Email inválido';
        }
        return undefined;

      case 'company':
        if (value && value.length > 100) {
          return 'La empresa no puede exceder 100 caracteres';
        }
        return undefined;

      case 'position':
        if (value && value.length > 100) {
          return 'El cargo no puede exceder 100 caracteres';
        }
        return undefined;

      default:
        return undefined;
    }
  }, []);

  /**
   * Valida todo el formulario
   */
  const validateForm = useCallback((): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    // Validar campos requeridos
    const nameError = validateField('name', formData.name);
    if (nameError) newErrors.name = nameError;

    const phoneError = validateField('phone', formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    // Validar campos opcionales
    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;

    const companyError = validateField('company', formData.company);
    if (companyError) newErrors.company = companyError;

    const positionError = validateField('position', formData.position);
    if (positionError) newErrors.position = positionError;

    return newErrors;
  }, [formData, validateField]);

  /**
   * Check si el formulario es válido
   */
  const isValid = useCallback((): boolean => {
    const validationErrors = validateForm();
    return Object.keys(validationErrors).length === 0 && !isDuplicate;
  }, [validateForm, isDuplicate]);

  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Marca un campo como tocado
   */
  const setFieldTouched = useCallback((field: keyof ContactFormData) => {
    setTouchedFields(prev => new Set(prev).add(field));
  }, []);

  /**
   * Maneja cambios en el formulario
   */
  const handleChange = useCallback((field: keyof ContactFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Marcar como tocado
    setFieldTouched(field);

    // Validar el campo
    const error = validateField(field, value);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));

    // Si es teléfono, verificar duplicados
    if (field === 'phone' && value && checkDuplicatePhone) {
      const normalized = normalizePhone(value);
      if (validatePhone(value)) {
        const duplicate = checkDuplicatePhone(normalized);
        setIsDuplicate(!!duplicate);
        setDuplicateContact(duplicate || null);
      } else {
        setIsDuplicate(false);
        setDuplicateContact(null);
      }
    }
  }, [validateField, setFieldTouched, checkDuplicatePhone]);

  /**
   * Resetea el formulario
   */
  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      company: '',
      position: '',
      groups: [],
      tags: [],
      status: 'active',
      notes: ''
    });
    setErrors({});
    setTouchedFields(new Set());
    setIsDuplicate(false);
    setDuplicateContact(null);
  }, []);

  /**
   * Submit del formulario
   */
  const handleSubmit = useCallback(async () => {
    // Marcar todos los campos como tocados
    setTouchedFields(new Set(['name', 'phone', 'email', 'company', 'position']));

    // Validar todo el formulario
    const validationErrors = validateForm();
    setErrors(validationErrors);

    // Si hay errores, no continuar
    if (Object.keys(validationErrors).length > 0) {
      if (onError) {
        onError('Por favor corrige los errores en el formulario');
      }
      return;
    }

    // Si hay duplicado, no continuar
    if (isDuplicate) {
      if (onError) {
        onError('Este teléfono ya existe en la base de datos');
      }
      return;
    }

    // Si no hay función addContact, no continuar
    if (!addContact) {
      if (onError) {
        onError('No se puede guardar el contacto');
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar datos para guardar
      const contactData: Omit<ContactDB, 'id' | 'created_at' | 'updated_at'> = {
        name: formData.name.trim(),
        phone: normalizePhone(formData.phone),
        email: formData.email.trim() || undefined,
        company: formData.company.trim() || undefined,
        positon: formData.position.trim() || undefined, // ← NOTA: ContactDB usa "positon" (typo)
        groups: formData.groups,
        tags: formData.tags,
        status: formData.status,
        source: 'manual', // ← Campo requerido en ContactDB
        notes: formData.notes.trim() || undefined,
        last_contacted: new Date().toISOString(), // ← ContactDB usa "last_contacted" no "last_interaction"
        metadata: {} // ← ContactDB usa "metadata" no "custom_fields"
      };

      // Guardar contacto
      const newContact = await addContact(contactData);

      // Success
      if (onSuccess) {
        onSuccess(newContact);
      }

      // Reset form
      resetForm();

    } catch (err: any) {
      console.error('Error saving contact:', err);
      if (onError) {
        onError(err.message || 'Error al guardar el contacto');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, isDuplicate, addContact, onSuccess, onError, resetForm]);

  // ───────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────────

  // Validar en tiempo real cuando cambian los datos
  useEffect(() => {
    if (touchedFields.size > 0) {
      const validationErrors = validateForm();
      setErrors(validationErrors);
    }
  }, [formData, touchedFields, validateForm]);

  // ───────────────────────────────────────────────────────────────────────
  // RETURN
  // ───────────────────────────────────────────────────────────────────────

  return {
    formData,
    errors,
    isSubmitting,
    isDuplicate,
    duplicateContact,
    
    handleChange,
    handleSubmit,
    resetForm,
    
    isValid: isValid(),
    touchedFields,
    setFieldTouched
  };
};

export default useContactForm;